var _dec, _class, _descriptor;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { deprecate } from '@ember/debug';
import { reads } from '@ember/object/computed';
import { resolve } from 'rsvp';
import { PromiseArrayProxy, PromiseObjectProxy } from './promise-proxy-base';

/**
  @module @ember-data/store
*/

/**
  A `PromiseArray` is an object that acts like both an `Ember.Array`
  and a promise. When the promise is resolved the resulting value
  will be set to the `PromiseArray`'s `content` property. This makes
  it easy to create data bindings with the `PromiseArray` that will be
  updated when the promise resolves.

  This class should not be imported and instantiated directly.

  For more information see the [Ember.PromiseProxyMixin
  documentation](/ember/release/classes/PromiseProxyMixin).

  Example

  ```javascript
  let promiseArray = PromiseArray.create({
    promise: $.getJSON('/some/remote/data.json')
  });

  promiseArray.get('length'); // 0

  promiseArray.then(function() {
    promiseArray.get('length'); // 100
  });
  ```

  @class PromiseArray
  @public
  @extends Ember.ArrayProxy
  @uses Ember.PromiseProxyMixin
*/

export let PromiseArray = (_dec = reads('content.meta'), _class = class PromiseArray extends PromiseArrayProxy {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "meta", _descriptor, this);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "meta", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class);

/**
  A `PromiseObject` is an object that acts like both an `EmberObject`
  and a promise. When the promise is resolved, then the resulting value
  will be set to the `PromiseObject`'s `content` property. This makes
  it easy to create data bindings with the `PromiseObject` that will
  be updated when the promise resolves.

  This class should not be imported and instantiated directly.

  For more information see the [Ember.PromiseProxyMixin
  documentation](/ember/release/classes/PromiseProxyMixin.html).

  Example

  ```javascript
  let promiseObject = PromiseObject.create({
    promise: $.getJSON('/some/remote/data.json')
  });

  promiseObject.get('name'); // null

  promiseObject.then(function() {
    promiseObject.get('name'); // 'Tomster'
  });
  ```

  @class PromiseObject
  @public
  @extends Ember.ObjectProxy
  @uses Ember.PromiseProxyMixin
*/
export { PromiseObjectProxy as PromiseObject };
export function promiseObject(promise, label) {
  return PromiseObjectProxy.create({
    promise: resolve(promise, label)
  });
}
export function promiseArray(promise, label) {
  return PromiseArray.create({
    promise: resolve(promise, label)
  });
}

// constructor is accessed in some internals but not including it in the copyright for the deprecation
const ALLOWABLE_METHODS = ['constructor', 'then', 'catch', 'finally'];
export function deprecatedPromiseObject(promise) {
  const promiseObjectProxy = promiseObject(promise);
  const handler = {
    get(target, prop, receiver) {
      if (!ALLOWABLE_METHODS.includes(prop)) {
        deprecate(`Accessing ${prop} is deprecated. The return type is being changed fomr PromiseObjectProxy to a Promise. The only available methods to access on this promise are .then, .catch and .finally`, false, {
          id: 'ember-data:model-save-promise',
          until: '5.0',
          for: '@ember-data/store',
          since: {
            available: '4.4',
            enabled: '4.4'
          }
        });
      }
      const value = Reflect.get(target, prop, receiver);
      if (value && typeof value === 'function' && typeof value.bind === 'function') {
        return value.bind(target);
      }
      return value;
    }
  };
  return new Proxy(promiseObjectProxy, handler);
}